import { FC } from "react";

import Stack from "@mui/material/Stack";
import { formatDateOnly } from "@simplyk/common";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Info } from "@/icons/outlined";

interface MemberDuplicateWarningProps {
  expirationDate: Date | string | null;
  isAutoRenewal: boolean;
}

export const MemberDuplicateWarning: FC<MemberDuplicateWarningProps> = ({ expirationDate, isAutoRenewal }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();

  const formattedDate = expirationDate
    ? formatDateOnly(expirationDate, locale, { year: "numeric", month: "long", day: "numeric" })
    : undefined;

  const warningText = (() => {
    if (!formattedDate) {
      return t("ticketing", "rate.item.activeMemberWarning.noExpiration");
    }
    if (isAutoRenewal) {
      return t("ticketing", "rate.item.activeMemberWarning.renewing", { date: formattedDate });
    }
    return t("ticketing", "rate.item.activeMemberWarning.expiring", { date: formattedDate });
  })();

  return (
    <Stack direction="row" gap={1} alignItems="center">
      <FormIcon size="small" sx={(theme) => ({ color: theme.palette.forms?.text.brand })}>
        <Info />
      </FormIcon>
      <FormTypography variant="body2" vibe="brand">
        {warningText}
      </FormTypography>
    </Stack>
  );
};
